/**
 * Shared constants for blog card components.
 *
 * What: Category color mappings and gradient definitions used across blog card variants.
 * Why: Keeps visual identity consistent between BlogPostCard and BlogFeaturedPost.
 * When: Update when adding new blog categories or changing category visual styles.
 */

import { colors } from '@/components/landing/landingTokens';

export interface CategoryColor {
  bg: string;
  text: string;
}

/**
 * Category chips take the landing wash tokens, with the dark step of each
 * accent as the label — orange and green at chip scale clear AA only there.
 * Secondary Math's sky pair is the one identity with no token: cobalt already
 * belongs to Primary Math, and collapsing the two would erase the distinction.
 */
export const CATEGORY_COLORS: Record<string, CategoryColor> = {
  'Learning & Mindset': {
    bg: colors.washAmber,
    text: colors.accentOrangeDark,
  },
  'Primary Math': { bg: colors.washCobalt, text: colors.accentCobalt },
  'Secondary Math': { bg: '#E0F2FE', text: '#0369A1' },
  'Choosing Math Tuition': {
    bg: colors.washGreen,
    text: colors.accentGreenDark,
  },
};

export const DEFAULT_CATEGORY_COLOR: CategoryColor = {
  bg: colors.backgroundAlt,
  text: colors.ink,
};

export const CATEGORY_GRADIENTS: Record<string, string> = {
  'Learning & Mindset': `linear-gradient(135deg, ${colors.accentOrange} 0%, ${colors.accentAmber} 100%)`,
  'Primary Math': `linear-gradient(135deg, ${colors.accentCobalt} 0%, ${colors.accentCobaltBright} 100%)`,
  'Secondary Math': `linear-gradient(135deg, ${colors.accentCobaltBright} 0%, #6A8FFF 100%)`,
  'Choosing Math Tuition': `linear-gradient(135deg, ${colors.accentGreenDark} 0%, ${colors.accentGreen} 100%)`,
};

export const DEFAULT_CATEGORY_GRADIENT = `linear-gradient(135deg, ${colors.accentOrange} 0%, ${colors.accentCobalt} 100%)`;

/** Shared "updated" indicator, on the landing palette's green. */
export const UPDATED_COLORS = {
  bg: colors.washGreen,
  text: colors.accentGreenDark,
} as const;
